import { Link } from 'react-router-dom';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { formatScoreFraction, formatScorePercentage } from '@/lib/assessment-score-display';

export interface PreviousAttemptRow {
  id: string;
  number: number;
  date: string;
  status: string;
  score: number | null;
  maxScore: number;
  result: 'pass' | 'fail' | 'in_progress' | null;
}

interface AssessmentConfirmPanelProps {
  title: string;
  previousAttempts: PreviousAttemptRow[];
  onStart: () => void;
  onViewAttempt?: (attemptId: string) => void;
  onEditAttempt?: (attemptId: string) => void;
  onDeleteAttempt?: (attemptId: string) => void;
  canManageAttempts?: boolean;
  deletingAttemptId?: string | null;
  starting?: boolean;
  backTo: string;
  backLabel?: string;
  hasActiveAttempt?: boolean;
  canAttempt?: boolean;
  canStartNewAttempt?: boolean;
  maxAttempts?: number | null;
  attemptsUsed?: number;
  pendingReview?: boolean;
  timeLimitSeconds?: number | null;
  showGrades?: boolean;
}

function timeLimitLabel(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (rest === 0) return `${minutes} min`;
  if (minutes === 0) return `${rest} s`;
  return `${minutes} min ${rest} s`;
}

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Finalizado',
  in_progress: 'En progreso',
  pending_review: 'En revisión',
};

const RESULT_LABELS: Record<string, string> = {
  pass: 'Aprobado',
  fail: 'Reprobado',
  in_progress: 'En progreso',
};

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function AssessmentConfirmPanel({
  title,
  previousAttempts,
  onStart,
  onViewAttempt,
  onEditAttempt,
  onDeleteAttempt,
  canManageAttempts = false,
  deletingAttemptId = null,
  starting = false,
  backTo,
  backLabel = 'Volver al curso',
  hasActiveAttempt = false,
  canAttempt = true,
  canStartNewAttempt = true,
  maxAttempts = null,
  attemptsUsed = 0,
  pendingReview = false,
  timeLimitSeconds = null,
  showGrades = true,
}: AssessmentConfirmPanelProps) {
  const showActions =
    onViewAttempt || (canManageAttempts && (onEditAttempt || onDeleteAttempt));
  const atAttemptLimit = !canStartNewAttempt && !hasActiveAttempt;
  const limitLabel = timeLimitLabel(timeLimitSeconds);
  const showAttemptLimit =
    maxAttempts !== null && maxAttempts > 0
      ? `Intentos permitidos: ${maxAttempts} · Usados: ${attemptsUsed}`
      : null;
  return (
    <div className="lms-assessment-confirm font-sans">
      <div className="lms-site-admin-hub-container py-8 md:py-12 lms-assessment-confirm__layout">
        <div className="lms-assessment-confirm__header mb-4 flex items-start gap-4">
          <HubBackLink to={backTo} label={backLabel} />
          <div className="min-w-0 flex-1">
            <h1 className="mb-2 text-3xl font-semibold text-dash-primary md:text-4xl">{title}</h1>
          </div>
        </div>

        <div className="lms-assessment-confirm__card">
          <div className="lms-assessment-confirm__card-accent" aria-hidden="true" />
          <div className="lms-assessment-confirm__icon-wrap">
            <span className="material-symbols-outlined" aria-hidden="true">warning</span>
          </div>
          <h2 className="lms-assessment-confirm__prompt">
            {!canAttempt
              ? 'Consulta de intentos en solo lectura.'
              : hasActiveAttempt
                ? 'Tienes un intento en progreso. ¿Deseas continuar donde lo dejaste?'
                : atAttemptLimit && pendingReview
                  ? 'Tu intento anterior está en revisión'
                  : atAttemptLimit
                    ? `Has alcanzado el número máximo de intentos permitidos (${maxAttempts ?? '—'}).`
                    : 'Usted está por comenzar su intento en esta evaluación. ¿Desea continuar?'}
          </h2>
          {showAttemptLimit && (
            <p className="lms-assessment-confirm__limit-note">{showAttemptLimit}</p>
          )}
          {limitLabel && (
            <p className="lms-assessment-confirm__limit-note">Tiempo límite: {limitLabel}</p>
          )}
          <div className="lms-assessment-confirm__actions">
            {canAttempt && !atAttemptLimit && (
              <button
                type="button"
                className="lms-assessment-confirm__btn lms-assessment-confirm__btn--primary"
                onClick={onStart}
                disabled={starting}
              >
                {starting ? 'Cargando…' : hasActiveAttempt ? 'Continuar intento' : 'Iniciar evaluación'}
              </button>
            )}
            <Link
              to={backTo}
              className="lms-assessment-confirm__btn lms-assessment-confirm__btn--outline"
            >
              {backLabel}
            </Link>
          </div>
        </div>

        <section className="lms-assessment-confirm__history">
          <h3 className="lms-assessment-confirm__history-title">Intentos previos</h3>
          <div className="lms-assessment-confirm__table-wrap">
            {previousAttempts.length === 0 ? (
              <p className="lms-assessment-confirm__empty-history">No hay intentos previos.</p>
            ) : (
              <table className="lms-assessment-confirm__table">
                <thead>
                  <tr>
                    <th>Intento</th>
                    <th>Fecha</th>
                    <th>Estado</th>
                    {showGrades && <th>Calificación</th>}
                    {showGrades && <th>%</th>}
                    <th>Resultado</th>
                    {showActions && <th>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {previousAttempts.map((attempt) => (
                    <tr key={attempt.id}>
                      <td className="font-medium">{attempt.number}</td>
                      <td className="lms-assessment-confirm__cell-muted">{formatDate(attempt.date)}</td>
                      <td>
                        <span className="lms-assessment-confirm__badge lms-assessment-confirm__badge--status">
                          {STATUS_LABELS[attempt.status] ?? attempt.status}
                        </span>
                      </td>
                      {showGrades && (
                        <td className="font-medium">{formatScoreFraction(attempt.score, attempt.maxScore)}</td>
                      )}
                      {showGrades && (
                        <td className="lms-assessment-confirm__cell-muted">
                          {formatScorePercentage(attempt.score, attempt.maxScore)}
                        </td>
                      )}
                      <td>
                        {attempt.result === 'pass' && (
                          <span className="lms-assessment-confirm__badge lms-assessment-confirm__badge--pass">
                            <span className="material-symbols-outlined" aria-hidden="true">check</span>
                            {RESULT_LABELS.pass}
                          </span>
                        )}
                        {attempt.result === 'fail' && (
                          <span className="lms-assessment-confirm__badge lms-assessment-confirm__badge--fail">
                            <span className="material-symbols-outlined" aria-hidden="true">close</span>
                            {RESULT_LABELS.fail}
                          </span>
                        )}
                        {attempt.result === 'in_progress' && (
                          <span className="lms-assessment-confirm__badge lms-assessment-confirm__badge--progress">
                            {RESULT_LABELS.in_progress}
                          </span>
                        )}
                        {attempt.result === null && (
                          <span className="lms-assessment-confirm__cell-muted">—</span>
                        )}
                      </td>
                      {showActions && (
                        <td>
                          <div className="lms-assessment-confirm__row-actions">
                            {attempt.status === 'submitted' && onViewAttempt && (
                              <button
                                type="button"
                                className="lms-assessment-confirm__icon-btn"
                                onClick={() => onViewAttempt(attempt.id)}
                                title="Ver intento"
                                aria-label="Ver intento"
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">visibility</span>
                              </button>
                            )}
                            {canManageAttempts && onEditAttempt && (
                              <button
                                type="button"
                                className="lms-assessment-confirm__icon-btn"
                                onClick={() => onEditAttempt(attempt.id)}
                                title="Editar intento"
                                aria-label="Editar intento"
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">edit</span>
                              </button>
                            )}
                            {canManageAttempts && onDeleteAttempt && (
                              <button
                                type="button"
                                className="lms-assessment-confirm__icon-btn lms-assessment-confirm__icon-btn--danger"
                                onClick={() => onDeleteAttempt(attempt.id)}
                                disabled={deletingAttemptId === attempt.id}
                                title="Eliminar intento"
                                aria-label="Eliminar intento"
                                aria-busy={deletingAttemptId === attempt.id}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  {deletingAttemptId === attempt.id ? 'progress_activity' : 'delete'}
                                </span>
                              </button>
                            )}
                            {attempt.status !== 'submitted' &&
                              !canManageAttempts &&
                              !onViewAttempt && (
                                <span className="lms-assessment-confirm__cell-muted">—</span>
                              )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
